/* eslint-disable max-statements */
import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'

import type { IUser } from '~/entities/user/user/interfaces/user'
import { useUsersStore } from '~/entities/user/user/stores/users'

import * as comment from '~/features-swym/comment/api/comments'
import type { IAnswer, IComment } from '~/features-swym/comment/interfaces/comment'

import {
  forwardMessage as forwardInstantMessage,
  forwardInstantMessageFromExternalDm,
  getCommunityTimeline,
  getCommunityTimelineTags,
  getCommunityTimelineWithFilters,
  postMessage,
} from '~/features-shared/content/api/contents'
import type { IContent } from '~/features-shared/content/interfaces/content'
import type { ANSWER_ID, COMMENT_ID, MESSAGE_ID, THREAD_ID } from '~/features-shared/content/stores/contents'
import { useContentsStore } from '~/features-shared/content/stores/contents'
import { mergeContent } from '~/features-shared/content/stores/contents-db'
import { useTaggable } from '~/features-shared/tags/composables/taggable'
import type { IMessageDetailsInternalEvent, ITimeline } from '~/features-shared/timeline/interfaces/timeline'
import { TimelineStatus } from '~/features-shared/timeline/interfaces/timeline'
import { getTemporaryTimelineMessage } from '~/features-shared/timeline/utils/temporary-message'

export const useContentsStoreV1 = defineStore('contents-v1', () => {
  const storeUsers = useUsersStore()
  const storeContents = useContentsStore()
  const taggable = useTaggable({}) // For global tags

  // #region STATE
  const messagesMap: Map<MESSAGE_ID, ITimeline> = reactive(new Map())
  const comments_id: Map<COMMENT_ID, MESSAGE_ID> = reactive(new Map())
  const answers_id: Map<ANSWER_ID, MESSAGE_ID> = reactive(new Map())
  const thread_timelines_id: Map<THREAD_ID, MESSAGE_ID[]> = reactive(new Map())
  // #endregion

  // #region COMPUTED
  const currentTimeline: ComputedRef<string[]> = computed(() => thread_timelines_id.get(storeContents._key) || [])
  const currentTimelineList = computed(() => currentTimeline.value
    // eslint-disable-next-line max-nested-callbacks
    .map(id => messagesMap.get(id))
    // eslint-disable-next-line max-nested-callbacks
    .filter(el => el) as ITimeline[])
  // #endregion

  // #region ACTIONS
  const _fetchTimelineWithFilters = async (refine: string[] = [], query = '') => {
    try {
      const response = await getCommunityTimelineWithFilters(
        storeContents.thread_selected,
        Array.from(currentTimelineList.value)
          .pop()?.id,
        undefined,
        { refine, query },
      )
      const messages = response.result

      if (response.tags)
        taggable.setRawTags?.(response)

      return messages
    }
    catch (err) {
      // Reset tags if the call fails
      taggable.setRawTags?.(null)
      throw err
    }
  }

  const fetchTimeline = async (refine: string[] = [], query = '') => {
    if (!currentTimeline.value || !currentTimeline.value.length)
      thread_timelines_id.set(storeContents._key, [])

    // Store them before the fetch to avoid push the result inside another timeline
    const timeline = currentTimeline.value
    const thread_id = storeContents.thread_selected

    let messages: ITimeline[] = []
    if (refine.length || query.length) {
      messages = await _fetchTimelineWithFilters(refine, query)
    }
    else {
      const from = Array.from(currentTimelineList.value)
        .pop()?.id
      messages = (await getCommunityTimeline(thread_id, from)).result

      if (!from)
        taggable.fetchTagsSeparately?.(() => getCommunityTimelineTags(thread_id))
    }

    messages.forEach((m) => {
      // if the message is a post, we merge the content into the store and keep only the URI
      if (m.message_type === 'INTERNAL_CONTENT') {
        const content = m.message_details as IContent
        mergeContent(content)
        m.message_details = { subject_uri: content.subject_uri }
      }
      mergeMessage(m)
      // In case of performance issues, check if we can optimise this check
      // this is to avoid duplicate items being appended to the timeline
      if (!timeline.includes(m.message_uri))
        timeline.push(m.message_uri)
    })

    return messages
  }

  const emptyTimeline = () => {
    thread_timelines_id.set(storeContents._key, [])
    messagesMap.clear()
    comments_id.clear()
    answers_id.clear()
  }

  // Remove existing messages and fetch new messages
  // TODO: Should we remove existing messages from messagesMap as well? OR empty the messagesMap?
  const refreshTimeline = async (refine: string[] = [], query = '') => {
    emptyTimeline()
    return fetchTimeline(refine, query)
  }

  // Fetch Time line routine for DM notification navigation
  const fetchTimelineAroundMessage = async (
    refine: string[] = [],
    query = '',
    from = '',
  ) => {
    emptyTimeline()
    if (!currentTimeline.value || !currentTimeline.value.length)
      thread_timelines_id.set(storeContents._key, [])

    // Store them before the fetch to avoid push the result inside another timeline
    const timeline = currentTimeline.value
    const thread_id = storeContents.thread_selected

    let messages: ITimeline[] = []
    if (refine.length || query.length) {
      messages = await _fetchTimelineWithFilters(refine, query)
    }
    else {
      const lastMsg = Array.from(currentTimelineList.value)
        .pop()?.id
      if (from) {
        messages = (await getCommunityTimeline(
          storeContents.thread_selected,
          from,
          0,
          20,
          20,
        )).result
      }

      else { messages = (await getCommunityTimeline(storeContents.thread_selected, lastMsg)).result }
      if (!lastMsg)
        taggable.fetchTagsSeparately?.(() => getCommunityTimelineTags(thread_id))
    }
    messages.forEach((m) => {
    // if the message is a post, we merge the content into the store and keep only the URI
      if (m.message_type === 'INTERNAL_CONTENT') {
        mergeContent(m.message_details)
        m.message_details = { subject_uri: m.message_details.subject_uri }
      }
      mergeMessage(m)
      // In case of performance issues, check if we can optimise this check
      // this is to avoid duplicate items being appended to the timeline
      if (!timeline.includes(m.message_uri))
        timeline.push(m.message_uri)
    })

    return messages
  }

  const fetchNextPage = (refine: string[] = [], query = '') => {
    return fetchTimeline(refine, query)
  }

  const mergeMessage = (message: Partial<ITimeline>) => {
    if (message.message_uri) {
      const c = messagesMap.get(message.message_uri) || {}
      messagesMap.set(message.message_uri, { ...c, ...message } as ITimeline)

      if (message.message_details && (message.message_details as IMessageDetailsInternalEvent).model === 'Comment')
        comments_id.set((message.message_details as IMessageDetailsInternalEvent).model_uri, message.message_uri)

      if (message.message_details && (message.message_details as IMessageDetailsInternalEvent).model === 'Answer')
        answers_id.set((message.message_details as IMessageDetailsInternalEvent).model_uri, message.message_uri)
    }
  }

  const addMessage = async (message: ITimeline) => {
    mergeMessage(message)
    if (!currentTimeline.value.includes(message.message_uri))
      currentTimeline.value.unshift(message.message_uri)

    if (message.author.login === storeUsers.currentUser?.login && message.triggered_by !== 'TMP_MESSAGE') {
      // try to find equivalent message
      const index = currentTimeline.value.findIndex((el) => {
        const content = messagesMap.get(el)

        if (content?.triggered_by === 'TMP_MESSAGE' && content.message_uri === message['X-Request-ID'])
          return true

        return false
      })

      // TMP_MESSAGE found, remove it
      if (index >= 0)
        removeMessage({ message_uri: currentTimeline.value[index] })
    }
  }

  const removeMessage = async (message: Partial<ITimeline>) => {
    if (message.subject_uri) {
      await comment.deleteComment(message.subject_uri.split(':comment:')
        .shift(), message.subject_uri)
    }

    const m = currentTimeline.value.find(el => el === message.message_uri)
    if (message.message_uri) {
      if (m)
        currentTimeline.value.splice(currentTimeline.value.indexOf(m), 1)

      if (message.status === TimelineStatus.SENT)
        messagesMap.delete(message.message_uri)
    }
  }

  const editMessage = async (message: Partial<ITimeline>, event: string) => {
    let response
    if (message.subject_uri) {
      response = await comment.editComment(
        event,
        message.subject_uri.split(':comment:')[0],
        message.subject_uri,
      )
    }

    if (message.message_uri) {
      const m = messagesMap.get(message.message_uri)
      if (m)
        m.message_details.content = response?.result.description
    }
  }

  const retryMessage = async (message: ITimeline) => {
    try {
      if (message?.message_details && 'content' in message.message_details) {
        const response = await postMessage(
          storeContents.thread_selected,
          message.message_details?.content,
          message.message_uri,
          storeUsers.currentUser as IUser,
          message.message_details.parent_message?.subject_uri,
        )
        message.status = TimelineStatus.SENT
        mergeMessage({ ...message, subject_uri: response.result.commentUri })
      }
    }
    catch (error) {
      message.status = TimelineStatus.ERROR
      mergeMessage(message)
      // removeMessage({ message_uri: item.message_uri })
    }
  }

  const createMessage = async (message: string, parent_comment?: ITimeline): Promise<ITimeline> => {
    const item = reactive(await getTemporaryTimelineMessage({
      external_app_id: '3DSWYM',
      currentTimelineId: storeContents.thread_selected,
      message_uri: crypto.randomUUID(),
      author: storeUsers.currentUser as IUser,
      message,
      parent_comment,
      status: TimelineStatus.PENDING,
    })) as ITimeline

    addMessage(item)

    try {
      // TODO refactor
      const response = await postMessage(
        storeContents.thread_selected,
        message,
        item.message_uri,
        storeUsers.currentUser as IUser,
        parent_comment?.subject_uri,
      )

      item.status = TimelineStatus.SENT
      mergeMessage({ ...item, subject_uri: response.result.commentUri })
    }
    catch (error) {
      item.status = TimelineStatus.ERROR
      mergeMessage(item)
      // removeMessage({ message_uri: item.message_uri })
    }

    return item
  }

  const getComments = async (subject_uri: string) => {
    return (await comment.getComments({ subject_uri })).result
  }

  const getCommentsStats = async (subject_uri: string) => {
    return (await comment.getCommentsStats({ subject_uri })).result
  }

  const addMyEndorse = async (subject_uri: string) => {
    return (await comment.addMyEndorse({ subject_uri })).result
  }

  const removeMyEndorse = async (subject_uri: string, endorse_uri: string) => {
    return (await comment.removeMyEndorse({ subject_uri, endorse_uri })).result
  }

  const getUsersWhoEndorse = async (params: Parameters<typeof comment.getUsersWhoEndorse>[0]) => {
    return (await comment.getUsersWhoEndorse(params)).result
  }

  const forwardMessage = (subject_uri: string, message: ITimeline) => {
    if (!storeContents.isActiveConversationFromV2) {
      // forward swym -> swym
      return forwardInstantMessage({
        threadUri: subject_uri,
        content: message,
      })
    }
    else {
      // forward rtconv -> swym
      return forwardInstantMessageFromExternalDm({
        threadUri: subject_uri,
        content: message,
        sourceConv: storeContents.thread_selected,
      })
    }
  }

  const updateComment = (comment: IComment) => {
    const messageUri = comments_id.get(comment.subject_uri)
    const message = messageUri && messagesMap.get(messageUri)

    if (message) {
      const messageDetails = message.message_details as IMessageDetailsInternalEvent
      messageDetails.model_content = comment.description
      messageDetails.modification_date = comment.modified_at
    }
  }

  const updateAnswer = (answer: Partial<IAnswer>) => {
    const messageUri = answer.subject_uri && answers_id.get(answer.subject_uri)
    const message = messageUri && messagesMap.get(messageUri)

    if (message && answer.description && answer.moderated_at) {
      const messageDetails = message.message_details as IMessageDetailsInternalEvent
      messageDetails.model_content = answer.description
      messageDetails.modification_date = answer.moderated_at
    }
  }
  // #endregion

  return {
    messagesMap,
    thread_timelines_id,

    // Tags
    tags: taggable.tags,
    tagsWithSubjectURIData: taggable.tagsWithSubjectURIData,
    tagsWithoutSubjectURIData: taggable.tagsWithoutSubjectURIData,
    // #region COMPUTED
    currentTimelineList,
    // #endregion

    // #region ACTIONS
    fetchTimeline,
    refreshTimeline,
    emptyTimeline,
    fetchNextPage,
    mergeMessage,
    addMessage,
    removeMessage,
    editMessage,
    retryMessage,
    createMessage,
    getComments,
    getCommentsStats,
    addMyEndorse,
    removeMyEndorse,
    getUsersWhoEndorse,
    fetchTimelineAroundMessage,
    forwardMessage,
    updateComment,
    updateAnswer,
    // #endregion
  }
})
